import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import TabsNav from "../../components/tabs/Tabs";
import SubscriptionTab from "../../components/Subscription/SubscriptionTab";
import PaymentMethodsTab from "../../components/Subscription/PaymentMethodsTab";
import TransactionsTab from "../../components/Subscription/TransactionsTab";
import { useNavigate } from "react-router-dom";
import { 
  useAdminGetPaymentMethodQuery, 
  useAdminGetPaymentStatusQuery, 
  useAdminGetSubscriptionQuery 
} from "../../services/organization/subscription";
import { toast } from "react-toastify";
import SuccessModal from "../../components/Modals/SuccessModal";

const TABS = {
  SUBSCRIPTION: 1,
  PAYMENT_METHODS: 2,
  TRANSACTIONS: 3,
};

const Subscription = () => {
  const { t } = useTranslation(["common","subscription"]);
  const { search } = useLocation();

  const getInitialTab = () => {
    const params = new URLSearchParams(search);
    return params.get("card_verification") === "1" ? TABS.PAYMENT_METHODS : TABS.SUBSCRIPTION;
  };

  const [currentTab, setCurrentTab] = useState(getInitialTab);
  const navigate = useNavigate();

  const [params] = useState(() => new URLSearchParams(window.location.search));
  
  const cardPmId = params.get("payment_method_id");
  const hasCardVerification = params.get("card_verification") === "1";
  const hasVerifiedCard = cardPmId ? sessionStorage.getItem(`verified_card_${cardPmId}`) : null;
  const [isPollingCard, setIsPollingCard] = useState(hasCardVerification && !hasVerifiedCard);

  const { refetch: refetchCardStatus } = useAdminGetPaymentMethodQuery(cardPmId, { 
    skip: !isPollingCard 
  });

  const retryTxnId = params.get("transaction_id");
  const retrySubId = params.get("subscription_id");
  const hasRetryVerification = params.get("retry_verification") === "1";
  const hasVerifiedRetry = retryTxnId ? sessionStorage.getItem(`verified_txn_${retryTxnId}`) : null;
  const [isPollingRetry, setIsPollingRetry] = useState(hasRetryVerification && !hasVerifiedRetry);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { refetch: refetchPaymentStatus } = useAdminGetPaymentStatusQuery(
    { subscriptionId: retrySubId, transactionId: retryTxnId }, 
    { skip: !isPollingRetry }
  );

  const { refetch: refetchSubscription } = useAdminGetSubscriptionQuery();

  const pollTimerRef = useRef(null);

  useEffect(() => {
    if (hasCardVerification || hasRetryVerification) {
      const cleanParams = new URLSearchParams(window.location.search);
      cleanParams.delete("card_verification");
      cleanParams.delete("payment_method_id");
      cleanParams.delete("tap_id");
      cleanParams.delete("retry_verification");
      cleanParams.delete("data");
      cleanParams.delete("transaction_id");
      cleanParams.delete("subscription_id");
      navigate({ search: cleanParams.toString() ? `?${cleanParams.toString()}` : "" }, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!isPollingCard) return;

    const poll = async () => {
      try {
        const result = await refetchCardStatus();
        const status = result?.data?.data?.status;

        if (status === "verified") {
          setIsPollingCard(false);
          if (cardPmId) sessionStorage.setItem(`verified_card_${cardPmId}`, "1");
          toast.success(t("subscription:paymentMethods.cardAdded", "Card added successfully"));
          refetchSubscription();
        } else if (status === "failed") {
          setIsPollingCard(false);
          toast.error(t("subscription:paymentMethods.cardFailed", "Card verification failed. Please try again."));
        } else {
          pollTimerRef.current = setTimeout(poll, 3000);
        }
      } catch {
        pollTimerRef.current = setTimeout(poll, 3000);
      }
    };

    poll();
    return () => clearTimeout(pollTimerRef.current);
  }, [isPollingCard]);

  // Polling Loop for Retry Verification
  useEffect(() => {
    if (!isPollingRetry) return;

    const poll = async () => {
      try {
        const result = await refetchPaymentStatus();
        const status = result?.data?.data?.status;

        if (status === "success") {
          setIsPollingRetry(false);
          if (retryTxnId) sessionStorage.setItem(`verified_txn_${retryTxnId}`, "1");
          setShowSuccessModal(true);
          refetchSubscription();
        } else if (status === "failed") {
          setIsPollingRetry(false);
          toast.error(t("subscription:retryPayment.failed", "Payment failed. Please try again."));
          refetchSubscription();
        } else {
          pollTimerRef.current = setTimeout(poll, 2000);
        }
      } catch {
        pollTimerRef.current = setTimeout(poll, 2000);
      }
    };

    poll();
    return () => clearTimeout(pollTimerRef.current);
  }, [isPollingRetry]);


  return (
    <div className="subscription-page">
      <div className="sticky-header no-padding-b">
        <div className="d-flex flex-wrap align-items-center">
          <div className="left-side">
            <h1 className="title-page">{t("common:subscription")}</h1>
          </div>
          <div className="ms-auto right-side">
            <ul className="list-filter">
              <li className="me-2"></li>
            </ul>
          </div>
        </div>
        <TabsNav
          activeIndex={currentTab - 1}
          tabsConfig={[
            {
              name: "subscription",
              label: t("subscription:tabs.subscriptions"),
              clickHandler: () => setCurrentTab(TABS.SUBSCRIPTION),
            },
            {
              name: "paymentmethods",
              label: t("subscription:tabs.paymentMethods"),
              clickHandler: () => setCurrentTab(TABS.PAYMENT_METHODS),
            },
            {
              name: "transactions",
              label: t("subscription:tabs.transactionHistory"),
              clickHandler: () => setCurrentTab(TABS.TRANSACTIONS),
            },
          ]}
        />
      </div>

      <div className="content-page__wrapper">
        {currentTab === TABS.SUBSCRIPTION && <SubscriptionTab />}
        {currentTab === TABS.PAYMENT_METHODS && <PaymentMethodsTab />}
        {currentTab === TABS.TRANSACTIONS && <TransactionsTab />}
      </div>

      <SuccessModal
        isOpened={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onButtonClick={() => setShowSuccessModal(false)}
        title={t("subscription:retryPayment.successTitle", "Payment Successful!")}
        description={t("subscription:retryPayment.successDesc", "Your payment has been processed successfully. Your subscription is now active.")}
        buttonLabel={t("common:done", "Done")}
      />

      {(isPollingCard || isPollingRetry) && (
        <div className="payment-processing-banner">
          <span className="payment-processing-banner__spinner" />
          <p>{t("subscription:retryPayment.processingVerification", "Verifying payment, please wait…")}</p>
        </div>
      )}
    </div>
  );
};

export default Subscription;
